import React, { useEffect, useState } from "react";

//UI Material
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  SwipeableDrawer,
  Typography,
} from "@mui/material";

//Material Icons
import { Add, Cancel, Inventory2 } from "@mui/icons-material";

//Local components
import Form from "../basics/Form";
import ModalWindow from "../Modal";

//Helpers
import { fetchWithTokenJSONPost } from "../../Helpers/network";
import { currencies } from "../../Constants/currencies";

export default function NewItemEntryDrawer({
  data = {},
  open,
  setOpen,
  getData,
  suppliersList = []
}) {
  // ==========>Component States<==========
  //State for item entry data
  const [itemEntryData, setItemEntryData] = useState({});
  //State for setting loader
  const [isLoading, setIsLoading] = useState(false);
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);

  const addEntryForm = [
    {
      selection: "textfield",
      id: "quantity",
      initialValue: itemEntryData.quantity,
      xs: 12,
      md: 4,
      label: "Cantidad de ítems",
      multiline: false,
      type: "number",
      inputProps: {},
      placeholder: "Cantidad de este ítem",
      content: [],
      style: {},
    },
    {
      selection: "selector",
      id: "supplier",
      initialValue: itemEntryData.supplier || 0,
      xs: 12,
      md: 8,
      label: "Proveedor",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "",
      content: suppliersList.map((sup) => sup.name),
      style: { minWidth: "100%" },
    },
    {
      selection: "textfield",
      id: "costPerUnit",
      initialValue: itemEntryData.costPerUnit,
      xs: 12,
      md: 4,
      label: "Costo unitario (opcional)",
      multiline: false,
      type: "number",
      inputProps: {},
      placeholder: "Costo por unidad",
      content: [],
      style: {},
    },
    {
      selection: "selector",
      id: "currency",
      initialValue: itemEntryData.currency || 0,
      xs: 12,
      md: 4,
      label: "Tipo de moneda",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "",
      content: currencies,
      style: { minWidth: "100%" },
    },
    {
      selection: "textfield",
      id: "taxesPercentage",
      initialValue: itemEntryData.taxesPercentage || 13,
      xs: 12,
      md: 4,
      label: "Porcentaje por impuestos (opcional)",
      multiline: false,
      type: "number",
      inputProps: {
        endAdornment: <InputAdornment>%</InputAdornment>,
      },
      placeholder: "Porcentaje por impuestos",
      content: [],
      style: {},
    },
    {
      selection: "selector",
      id: "taxesIncluded",
      initialValue: itemEntryData.taxesIncluded || 0,
      xs: 12,
      md: 4,
      label: "¿Los impuestos están incluídos en el costo unitario?",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "",
      content: ["Sí", "No"],
      style: { minWidth: "100%" },
    },
    {
      selection: "textfield",
      id: "seller",
      initialValue: itemEntryData.seller,
      xs: 12,
      md: 4,
      label: "Vendedor (opcional)",
      multiline: false,
      type: "text",
      inputProps: {},
      placeholder: "Nombre del vendedor",
      content: [],
      style: {},
    },
    {
      selection: "textfield",
      id: "purchaseOrderNumber",
      initialValue: itemEntryData.purchaseOrderNumber,
      xs: 12,
      md: 4,
      label: "Órden de compra (opcional)",
      multiline: false,
      type: "text",
      inputProps: {},
      placeholder: "Número de órden de compra",
      content: [],
      style: {},
    },
    {
      selection: "textfield",
      id: "additionalInfo",
      initialValue: itemEntryData.additionalInfo,
      xs: 12,
      md: 12,
      label: "Información adicional (opcional)",
      multiline: false,
      type: "text",
      inputProps: {},
      placeholder: "Información adicional",
      content: [],
      style: {},
    },
  ];

  //Function to create new item entry
  const setItemEntry = async () => {
    setIsLoading(true);

    const newItemEntry = {
      id: data.id,
      name: data.name,
      quantity: Number(itemEntryData.quantity),
      supplier: suppliersList[itemEntryData.supplier].name,
      supplierId: suppliersList[itemEntryData.supplier].id,
      costPerUnit: itemEntryData.costPerUnit,
      currency: currencies[itemEntryData.currency],
      taxesPercentage: itemEntryData.taxesPercentage,
      taxesIncluded: itemEntryData.taxesIncluded === 0,
      seller: itemEntryData.seller,
      purchaseOrderNumber: itemEntryData.purchaseOrderNumber,
      additionalInfo: itemEntryData.additionalInfo,
    };

    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/inventory/create-item-entry",
        newItemEntry
      );
      const jsonResponse = await requestResponse.json();

      if (jsonResponse.status === "ok") {
        setModalText("Entrada de ítem registrada exitosamente.");
        setModalDescription(jsonResponse.description);
        getData();
        setOpen(false);
      } else {
        setModalText("No se pudo realizar la acción.");
        setModalDescription(jsonResponse.description);
      }
    } catch (error) {
      console.log(error);
    }
    setModalOpen(true);
    setIsLoading(false);
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      transitionDuration={600}
      disableSwipeToOpen={true}
      open={open}
      sx={{
        zIndex: "99",
        "& .MuiDrawer-paper": {
          justifyContent: "center",
          borderRadius: "35px 35px 0px 0px",
        },
      }}
      onClose={() => setOpen(false)}
      onOpen={() => {
        /**empty arrow function */
      }}
    >
      <Grid
        container
        justifyContent="center"
        sx={{ padding: "15px" }}
        textAlign="center"
        spacing={1}
      >
        <Grid item xs={12} sx={{ marginTop: "10px" }}>
          <Inventory2 color="success" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h7" fontWeight="bold" color="green">
            {`Nueva entrada de ítem`}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight="bold">
            {data.name}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">{`Identificador: ${data.id}`}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Form data={addEntryForm} handleStateChange={setItemEntryData} />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="warning" variant="filled">
            Por favor asegúrese de que la información que ingresó sobre el costo
            unitario y los impuestos esté correcta. Esta información será
            utilizada para el análisis de costos del inventario.
          </Alert>
        </Grid>

        <Grid item xs={12} sx={{ marginTop: "10px" }}>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              size="small"
              variant="contained"
              color="error"
              sx={{ color: "white" }}
              endIcon={<Cancel />}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancelar
            </Button>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                sx={{ color: "white" }}
                endIcon={<Add />}
                disabled={itemEntryData.name === ""}
                onClick={setItemEntry}
              >
                Registrar entrada
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
      <ModalWindow
        isOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={modalText}
        text={modalDescription}
        leftText="OK"
        leftClick={() => {
          setModalOpen(false);
        }}
      />
    </SwipeableDrawer>
  );
}
